import * as THREE from 'three'
import type RAPIER from '@dimforge/rapier3d-compat'
import { KeycardInventory, type KeycardColor } from './keycards'
import { sfx } from '../audio/sfx'
import { createAuthenticDoorMaterial } from '../level/textures'

export type DoorState = 'closed' | 'opening' | 'open' | 'closing' | 'locked'

export interface SlidingDoorConfig {
  id: string
  position: THREE.Vector3
  width?: number
  height?: number
  depth?: number
  clearanceMargin?: number
  slideAxis?: 'x' | 'z'
  slideDirection?: 1 | -1
  slideDistance?: number
  openSpeed?: number
  closeDelay?: number
  requiredKeycard?: KeycardColor
}

export class SlidingDoor {
  readonly id: string
  readonly position: THREE.Vector3
  readonly width: number
  readonly height: number
  readonly depth: number
  readonly clearanceMargin: number
  readonly slideAxis: 'x' | 'z'
  readonly slideDirection: 1 | -1
  readonly slideDistance: number
  readonly openSpeed: number
  readonly closeDelay: number
  readonly requiredKeycard?: KeycardColor

  state: DoorState = 'closed'
  openProgress = 0.0 // 0 = closed, 1 = fully open
  private waitToCloseTimer = 0

  // 3D & Rapier handles
  leftMesh: THREE.Mesh | null = null
  rightMesh: THREE.Mesh | null = null
  leftBody: RAPIER.RigidBody | null = null
  rightBody: RAPIER.RigidBody | null = null

  constructor(config: SlidingDoorConfig) {
    this.id = config.id
    this.position = config.position.clone()
    this.width = config.width ?? 3.0
    this.height = config.height ?? 3.2
    this.depth = config.depth ?? 0.35
    this.clearanceMargin = config.clearanceMargin ?? 0.02
    this.slideAxis = config.slideAxis ?? 'x'
    this.slideDirection = config.slideDirection ?? -1
    this.slideDistance = config.slideDistance ?? 1.6
    this.openSpeed = config.openSpeed ?? 2.0
    this.closeDelay = config.closeDelay ?? 1.5
    this.requiredKeycard = config.requiredKeycard

    if (this.requiredKeycard) {
      this.state = 'locked'
    }
  }

  canOpen(keycards?: KeycardInventory): boolean {
    if (!this.requiredKeycard) return true
    return keycards?.hasKeycard(this.requiredKeycard) ?? false
  }

  update(dt: number, inProximity: boolean, keycards?: KeycardInventory): void {
    const authorized = this.canOpen(keycards)

    if (inProximity) {
      if (!authorized) {
        this.state = 'locked'
        return
      }

      this.waitToCloseTimer = 0
      if (this.openProgress < 1.0) {
        if (this.openProgress === 0) {
          sfx.playDoorServo()
        }
        this.state = 'opening'
        this.openProgress = Math.min(1.0, this.openProgress + this.openSpeed * dt)
        if (this.openProgress >= 1.0) {
          this.state = 'open'
        }
      } else {
        this.state = 'open'
      }
    } else {
      if (this.openProgress > 0) {
        this.waitToCloseTimer += dt
        if (this.waitToCloseTimer >= this.closeDelay) {
          this.state = 'closing'
          this.openProgress = Math.max(0, this.openProgress - this.openSpeed * dt)
          if (this.openProgress <= 0) {
            this.state = authorized ? 'closed' : 'locked'
          }
        }
      } else {
        this.state = authorized ? 'closed' : 'locked'
      }
    }

    // Update 3D meshes & physics bodies if attached
    const offset = this.openProgress * this.slideDistance
    const isFullyOpen = this.openProgress >= 0.8
    if (this.leftMesh) {
      this.leftMesh.visible = this.openProgress < 0.99
    }
    if (this.rightMesh) {
      this.rightMesh.visible = this.openProgress < 0.99
    }

    const isBiparting = !!(this.leftMesh && this.rightMesh)
    const colliderY = isFullyOpen ? -50 : (this.position.y + this.height * 0.5)

    if (this.slideAxis === 'x') {
      const leftX = isBiparting
        ? this.position.x - this.width * 0.25 - offset
        : this.position.x + offset * this.slideDirection
      const rightX = isBiparting
        ? this.position.x + this.width * 0.25 + offset
        : this.position.x - offset * this.slideDirection

      if (this.leftMesh) this.leftMesh.position.x = leftX
      if (this.rightMesh) this.rightMesh.position.x = rightX
      if (this.leftBody) {
        this.leftBody.setNextKinematicTranslation({
          x: leftX,
          y: colliderY,
          z: this.position.z,
        })
      }
      if (this.rightBody) {
        this.rightBody.setNextKinematicTranslation({
          x: rightX,
          y: colliderY,
          z: this.position.z,
        })
      }
    } else {
      const leftZ = isBiparting
        ? this.position.z - this.width * 0.25 - offset
        : this.position.z + offset * this.slideDirection
      const rightZ = isBiparting
        ? this.position.z + this.width * 0.25 + offset
        : this.position.z - offset * this.slideDirection

      if (this.leftMesh) this.leftMesh.position.z = leftZ
      if (this.rightMesh) this.rightMesh.position.z = rightZ
      if (this.leftBody) {
        this.leftBody.setNextKinematicTranslation({
          x: this.position.x,
          y: colliderY,
          z: leftZ,
        })
      }
      if (this.rightBody) {
        this.rightBody.setNextKinematicTranslation({
          x: this.position.x,
          y: colliderY,
          z: rightZ,
        })
      }
    }
  }
}

import { colliderRegistry } from './collider-registry'

export function createDoorInstance(
  scene: THREE.Scene,
  world: RAPIER.World,
  RAPIER_REF: typeof RAPIER,
  config: SlidingDoorConfig,
): SlidingDoor {
  const door = new SlidingDoor(config)
  const panelW = door.width * 0.5
  const h = door.height
  const d = door.depth
  const clearance = door.clearanceMargin
  const leafDepth = Math.max(0.05, d - clearance * 2)
  const isZ = door.slideAxis === 'z'

  const panelGeo = isZ
    ? new THREE.BoxGeometry(leafDepth, h, panelW)
    : new THREE.BoxGeometry(panelW, h, leafDepth)

  const panelColor = door.requiredKeycard === 'red' ? 0xff4444 : 0xffffff
  const panelMat = createAuthenticDoorMaterial()
  panelMat.color.setHex(panelColor)

  const leftX = isZ ? door.position.x : door.position.x - panelW * 0.5
  const leftZ = isZ ? door.position.z - panelW * 0.5 : door.position.z
  const rightX = isZ ? door.position.x : door.position.x + panelW * 0.5
  const rightZ = isZ ? door.position.z + panelW * 0.5 : door.position.z

  // Left Panel
  const leftMesh = new THREE.Mesh(panelGeo, panelMat)
  leftMesh.position.set(leftX, door.position.y + h * 0.5, leftZ)
  scene.add(leftMesh)
  door.leftMesh = leftMesh

  const leftBodyDesc = RAPIER_REF.RigidBodyDesc.kinematicPositionBased().setTranslation(
    leftX,
    door.position.y + h * 0.5,
    leftZ,
  )
  const leftBody = world.createRigidBody(leftBodyDesc)
  const colHalfX = isZ ? d * 0.5 : panelW * 0.5
  const colHalfZ = isZ ? panelW * 0.5 : d * 0.5
  const leftCol = world.createCollider(RAPIER_REF.ColliderDesc.cuboid(colHalfX, h * 0.5, colHalfZ), leftBody)
  colliderRegistry.register(leftCol.handle, { id: door.id, name: `${door.id} (Left Leaf)`, category: 'door' })
  door.leftBody = leftBody

  // Right Panel
  const rightMesh = new THREE.Mesh(panelGeo.clone(), panelMat.clone())
  rightMesh.position.set(rightX, door.position.y + h * 0.5, rightZ)
  scene.add(rightMesh)
  door.rightMesh = rightMesh

  const rightBodyDesc = RAPIER_REF.RigidBodyDesc.kinematicPositionBased().setTranslation(
    rightX,
    door.position.y + h * 0.5,
    rightZ,
  )
  const rightBody = world.createRigidBody(rightBodyDesc)
  const rightCol = world.createCollider(RAPIER_REF.ColliderDesc.cuboid(colHalfX, h * 0.5, colHalfZ), rightBody)
  colliderRegistry.register(rightCol.handle, { id: door.id, name: `${door.id} (Right Leaf)`, category: 'door' })
  door.rightBody = rightBody

  return door
}
